import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  Search,
  UserCheck,
  UserX,
  Users,
  TrendingUp,
  Bell,
  Send,
  X,
  Eye,
  Filter,
  ArrowUpDown,
  Calendar,
  Clock,
  ShieldAlert,
  Globe,
  Smartphone,
  ExternalLink,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Customer, CustomerStats } from '@/types/customer';
import { customerApi } from '@/services/customerApi';
import { notificationApi } from '@/services/notificationApi';
import toast from 'react-hot-toast';

export const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats>({
    totalCustomers: 0,
    activeCustomers: 0,
    newThisMonth: 0,
    blockedCustomers: 0,
  });
  const [loading, setLoading] = useState(true);

  // Filters & Sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest'); // default: newest registered first

  // Modals
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [targetCustomer, setTargetCustomer] = useState<Customer | null>(null);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [sendingPush, setSendingPush] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [customersData, statsData] = await Promise.all([
        customerApi.getCustomers(),
        customerApi.getCustomerStats(),
      ]);
      setCustomers(customersData);
      setStats(statsData);
    } catch (error: any) {
      console.error('Error loading customers:', error);
      toast.error('Müşteri verileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Robust Date Parser for Firestore Timestamps, ISO Strings, Numbers, Date Objects
  const parseDate = (dateVal: any): Date | null => {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;
    if (typeof dateVal === 'number') {
      return new Date(dateVal < 10000000000 ? dateVal * 1000 : dateVal);
    }
    if (typeof dateVal === 'string') {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof dateVal === 'object') {
      const seconds = dateVal.seconds ?? dateVal._seconds;
      if (typeof seconds === 'number') {
        return new Date(seconds * 1000);
      }
      if (typeof dateVal.toDate === 'function') {
        try {
          return dateVal.toDate();
        } catch {}
      }
    }
    return null;
  };

  const getTime = (dateVal: any): number => {
    const d = parseDate(dateVal);
    return d ? d.getTime() : 0;
  };

  const formatDate = (dateValue?: any) => {
    const d = parseDate(dateValue);
    if (!d) return '-';
    return d.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateValue?: any) => {
    const d = parseDate(dateValue);
    if (!d) return '-';
    return d.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter & Sort Logic (Frontend)
  const filteredAndSortedCustomers = useMemo(() => {
    let result = [...customers];

    // 1. Search Filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.email?.toLowerCase().includes(term) ||
          c.phone?.includes(term) ||
          c.id?.toLowerCase().includes(term) ||
          c.addresses?.some(
            (a) => a.city?.toLowerCase().includes(term) || a.district?.toLowerCase().includes(term)
          )
      );
    }

    // 2. Auth Provider Filter
    if (providerFilter !== 'all') {
      result = result.filter((c) => {
        const provider = (c.authProvider || 'password').toLowerCase();
        if (providerFilter === 'google') return provider.includes('google');
        if (providerFilter === 'apple') return provider.includes('apple');
        if (providerFilter === 'password') return provider.includes('password') || provider === 'email';
        return true;
      });
    }

    // 3. Status Filter
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }

    // 4. Sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return getTime(b.createdAt) - getTime(a.createdAt);
        case 'oldest':
          return getTime(a.createdAt) - getTime(b.createdAt);
        case 'last_updated':
          return getTime(b.updatedAt || b.createdAt) - getTime(a.updatedAt || a.createdAt);
        case 'most_orders':
          return (b.totalOrders || 0) - (a.totalOrders || 0);
        case 'most_spent':
          return (b.totalSpent || 0) - (a.totalSpent || 0);
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        default:
          return getTime(b.createdAt) - getTime(a.createdAt);
      }
    });

    return result;
  }, [customers, searchTerm, providerFilter, statusFilter, sortBy]);

  const formatCurrency = (amount?: number) => {
    if (!amount) return '₺0';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'Aktif', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700' },
      inactive: { label: 'Pasif', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-700' },
      blocked: { label: 'Engelli', className: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-300 dark:border-rose-700' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getAuthProviderBadge = (provider?: string) => {
    const p = (provider || 'password').toLowerCase();
    if (p.includes('google')) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20 px-2 py-0.5 rounded-full font-medium">
          <Globe className="w-3 h-3" /> Google
        </span>
      );
    }
    if (p.includes('apple')) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] bg-gray-100 dark:bg-gray-500/10 text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-500/20 px-2 py-0.5 rounded-full font-medium">
          <Smartphone className="w-3 h-3" /> Apple
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20 px-2 py-0.5 rounded-full font-medium">
        <Mail className="w-3 h-3" /> E-Posta
      </span>
    );
  };

  const handleStatusUpdate = async (customerId: string, status: 'active' | 'inactive' | 'blocked') => {
    try {
      setUpdatingStatus(true);
      await customerApi.updateCustomerStatus(customerId, status);
      toast.success('Müşteri durumu güncellendi');
      setCustomers((prev) =>
        prev.map((c) => (c.id === customerId ? { ...c, status } : c))
      );
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer((prev) => (prev ? { ...prev, status } : null));
      }
    } catch (err: any) {
      toast.error('Durum güncellenemedi');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) {
      toast.error('Lütfen başlık ve mesaj alanlarını doldurun');
      return;
    }

    try {
      setSendingPush(true);
      if (targetCustomer) {
        await notificationApi.sendUserPush(targetCustomer.id, {
          title: pushTitle,
          body: pushBody,
          userType: 'customer',
        });
        toast.success(`Bildirim ${targetCustomer.name || 'müşteriye'} başarıyla gönderildi.`);
      } else {
        await notificationApi.sendBroadcastPush({
          title: pushTitle,
          body: pushBody,
          targetRole: 'customer',
        });
        toast.success('Tüm müşterilere toplu bildirim başarıyla gönderildi.');
      }
      setPushModalOpen(false);
      setTargetCustomer(null);
      setPushTitle('');
      setPushBody('');
    } catch (err: any) {
      toast.error(`Hata: ${err.message || 'Bildirim gönderilemedi'}`);
    } finally {
      setSendingPush(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Müşteri Yönetimi</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Kayıtlı müşteriler, anlık durum takibi ve push bildirim yönetimi
          </p>
        </div>
        <button
          onClick={() => {
            setTargetCustomer(null);
            setPushModalOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Bell className="w-4 h-4" />
          Toplu Push Bildirim Gönder
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Müşteri</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{stats.totalCustomers}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300">
              <Users className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Aktif Müşteriler</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{stats.activeCustomers}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-300">
              <UserCheck className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bu Ay Kaydolan</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{stats.newThisMonth}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-300">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Engellenen</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{stats.blockedCustomers}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-300">
              <UserX className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filter & Search Bar */}
      <div className="card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Box */}
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="İsim, email, telefon veya il/ilçe ara..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Provider Filter */}
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1 font-semibold">
              <Globe className="w-3.5 h-3.5" /> Giriş Yöntemi
            </div>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="input-field py-2 text-sm"
            >
              <option value="all">Tüm Yöntemler</option>
              <option value="password">📧 E-Posta / Şifre</option>
              <option value="google">🌐 Google</option>
              <option value="apple">🍎 Apple</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1 font-semibold">
              <Filter className="w-3.5 h-3.5" /> Hesap Durumu
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field py-2 text-sm"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">✅ Aktif</option>
              <option value="inactive">⛔ Pasif</option>
              <option value="blocked">🛑 Engelli</option>
            </select>
          </div>
        </div>

        {/* Sorting Row */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-800 gap-3 text-xs">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <ArrowUpDown className="w-4 h-4 text-indigo-500" />
            <span>Sıralama Ölçütü:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 font-medium text-gray-800 dark:text-gray-200 focus:outline-none focus:border-indigo-500"
            >
              <option value="newest">📅 Kayıt Tarihi (En Yeni → En Eski)</option>
              <option value="oldest">📅 Kayıt Tarihi (En Eski → En Yeni)</option>
              <option value="last_updated">⏱️ Son Güncellenme Tarihi</option>
              <option value="most_orders">🛍️ En Çok Sipariş Verenler</option>
              <option value="most_spent">💰 En Yüksek Harcama</option>
              <option value="name_asc">🔤 İsim (A-Z)</option>
              <option value="name_desc">🔤 İsim (Z-A)</option>
            </select>
          </div>

          <div className="text-gray-500 dark:text-gray-400 text-xs font-medium">
            Toplam <span className="font-bold text-indigo-600 dark:text-indigo-400">{filteredAndSortedCustomers.length}</span> müşteri listeleniyor
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-800/80 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3.5">Müşteri</th>
                <th className="px-6 py-3.5">İletişim</th>
                <th className="px-6 py-3.5">Şehir / Adres</th>
                <th className="px-6 py-3.5">Sipariş & Harcama</th>
                <th className="px-6 py-3.5">Kayıt Tarihi</th>
                <th className="px-6 py-3.5">Güncellenme</th>
                <th className="px-6 py-3.5">Durum</th>
                <th className="px-6 py-3.5 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800 text-sm">
              {filteredAndSortedCustomers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'Arama kriterlerine uygun müşteri bulunamadı' : 'Henüz müşteri kayıtlı değil'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded-full flex items-center justify-center font-bold text-sm border border-indigo-200 dark:border-indigo-700">
                          {customer.name ? customer.name.charAt(0).toUpperCase() : 'M'}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            {customer.name || 'İsimsiz Müşteri'}
                          </div>
                          <div className="mt-0.5">{getAuthProviderBadge(customer.authProvider)}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-900 dark:text-gray-200 flex items-center gap-1.5 font-medium">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        {customer.email || '-'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-1">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        {customer.phone || '-'}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-xs text-gray-800 dark:text-gray-200 font-medium">
                        <MapPin className="w-3.5 h-3.5 text-indigo-500" />
                        {customer.addresses?.[0]?.city || customer.addresses?.[0]?.province || '-'}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5 truncate max-w-[140px]">
                        {customer.addresses?.[0]?.district ? `${customer.addresses[0].district}` : ''}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                        {customer.totalOrders || 0} sipariş
                      </div>
                      <div className="text-xs text-indigo-600 dark:text-indigo-400 font-bold mt-0.5">
                        {formatCurrency(customer.totalSpent)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1 font-medium">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(customer.createdAt)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(customer.updatedAt || customer.createdAt)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(customer.status)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setDetailModalOpen(true);
                          }}
                          className="px-2.5 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-lg border border-indigo-200 dark:border-indigo-800 transition flex items-center gap-1 font-semibold"
                          title="Hızlı Detay Göster"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Detay
                        </button>

                        <button
                          onClick={() => {
                            setTargetCustomer(customer);
                            setPushModalOpen(true);
                          }}
                          className="p-1.5 text-gray-500 hover:text-pink-600 dark:hover:text-pink-400 rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/20 transition"
                          title="Özel Push Bildirim Gönder"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODAL: MÜŞTERİ HIZLI DETAY PENCERESİ (THEME ADAPTIVE) ================= */}
      {detailModalOpen && selectedCustomer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col text-gray-900 dark:text-gray-100">
            {/* Modal Header */}
            <div className="p-6 bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-600/20 border border-indigo-200 dark:border-indigo-500/40 text-indigo-600 dark:text-indigo-300 font-bold flex items-center justify-center text-lg shadow-sm">
                  {selectedCustomer.name ? selectedCustomer.name.charAt(0).toUpperCase() : 'M'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedCustomer.name || 'İsimsiz Müşteri'}</h2>
                    {getStatusBadge(selectedCustomer.status)}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">UID: {selectedCustomer.id}</span>
                    {getAuthProviderBadge(selectedCustomer.authProvider)}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setDetailModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Quick Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl border border-gray-200 dark:border-gray-700/60 space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İletişim Bilgileri</h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                      <Mail className="w-4 h-4 text-indigo-500" />
                      <span>{selectedCustomer.email || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                      <Phone className="w-4 h-4 text-indigo-500" />
                      <span>{selectedCustomer.phone || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl border border-gray-200 dark:border-gray-700/60 space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarih Bilgileri</h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                      <Calendar className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Kayıt:</span>
                      <span className="font-medium">{formatDateTime(selectedCustomer.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Son Güncelleme:</span>
                      <span className="font-medium">{formatDateTime(selectedCustomer.updatedAt || selectedCustomer.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics & Totals */}
              <div className="grid grid-cols-2 gap-4 bg-indigo-50/50 dark:bg-gray-800/40 p-4 rounded-xl border border-indigo-100 dark:border-gray-700/50">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Toplam Verilen Sipariş</p>
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">{selectedCustomer.totalOrders || 0} Adet</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Toplam Harcama Tutarı</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{formatCurrency(selectedCustomer.totalSpent)}</p>
                </div>
              </div>

              {/* Account Status Actions */}
              <div className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl border border-gray-200 dark:border-gray-700/60 space-y-2">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hesap Durumu Değiştir</h3>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    disabled={updatingStatus || selectedCustomer.status === 'active'}
                    onClick={() => handleStatusUpdate(selectedCustomer.id, 'active')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                      selectedCustomer.status === 'active'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-600/20 hover:text-emerald-700'
                    }`}
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Aktif Yap
                  </button>

                  <button
                    disabled={updatingStatus || selectedCustomer.status === 'inactive'}
                    onClick={() => handleStatusUpdate(selectedCustomer.id, 'inactive')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                      selectedCustomer.status === 'inactive'
                        ? 'bg-gray-600 text-white shadow-sm'
                        : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                    }`}
                  >
                    <AlertCircle className="w-3.5 h-3.5" /> Pasif Yap
                  </button>

                  <button
                    disabled={updatingStatus || selectedCustomer.status === 'blocked'}
                    onClick={() => handleStatusUpdate(selectedCustomer.id, 'blocked')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                      selectedCustomer.status === 'blocked'
                        ? 'bg-rose-600 text-white shadow-sm'
                        : 'bg-gray-200 dark:bg-gray-800 text-rose-600 dark:text-rose-400 hover:bg-rose-600/20'
                    }`}
                  >
                    <ShieldAlert className="w-3.5 h-3.5" /> Hesabı Engelle
                  </button>
                </div>
              </div>

              {/* Addresses List */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-indigo-500" /> Kayıtlı Adresler ({selectedCustomer.addresses?.length || 0})
                </h3>

                {!selectedCustomer.addresses || selectedCustomer.addresses.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">Kayıtlı adres bulunmuyor.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCustomer.addresses.map((addr, idx) => (
                      <div
                        key={addr.id || idx}
                        className="bg-gray-50 dark:bg-gray-800/80 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700/60 space-y-1 relative"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-xs text-gray-900 dark:text-white">{addr.title || `Adres ${idx + 1}`}</span>
                          {addr.isDefault && (
                            <span className="text-[10px] bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/30 px-1.5 py-0.5 rounded font-medium">
                              Varsayılan
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                          {addr.province || addr.city} / {addr.district}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{addr.details}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notification Preferences */}
              {selectedCustomer.preferences && (
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bildirim İzinleri</h3>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span
                      className={`px-2.5 py-1 rounded-full border ${
                        selectedCustomer.preferences.pushNotifications
                          ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      Push: {selectedCustomer.preferences.pushNotifications ? 'Açık' : 'Kapalı'}
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full border ${
                        selectedCustomer.preferences.emailNotifications
                          ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      E-Posta: {selectedCustomer.preferences.emailNotifications ? 'Açık' : 'Kapalı'}
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full border ${
                        selectedCustomer.preferences.promotions
                          ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      Kampanya: {selectedCustomer.preferences.promotions ? 'Açık' : 'Kapalı'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <button
                onClick={() => {
                  setDetailModalOpen(false);
                  setTargetCustomer(selectedCustomer);
                  setPushModalOpen(true);
                }}
                className="px-4 py-2 bg-pink-50 dark:bg-pink-600/20 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-600/30 border border-pink-200 dark:border-pink-500/30 rounded-lg text-xs font-semibold transition flex items-center gap-1.5"
              >
                <Bell className="w-4 h-4" /> Özel Push Bildirim Gönder
              </button>

              <button
                onClick={() => {
                  setDetailModalOpen(false);
                  navigate(`/admin/customers/${selectedCustomer.id}`);
                }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-semibold transition flex items-center gap-1.5 shadow-md shadow-indigo-600/20"
              >
                Tüm Detay & Sipariş Geçmişi <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Modal */}
      {pushModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 text-gray-900 dark:text-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-pink-100 dark:bg-pink-500/20 text-pink-600 dark:text-pink-400 rounded-lg border border-pink-200 dark:border-pink-500/30">
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    {targetCustomer ? 'Özel Push Bildirim' : 'Toplu Push Bildirim Gönder'}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {targetCustomer
                      ? `Alıcı: ${targetCustomer.name || targetCustomer.email}`
                      : 'Tüm mobil müşterilere anında iletilecektir'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setPushModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                Bildirim Başlığı
              </label>
              <input
                type="text"
                placeholder="Örn: Özel İndirim Fırsatı! 🌸"
                className="input-field"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                Mesaj İçeriği
              </label>
              <textarea
                rows={3}
                placeholder="Örn: Bugün vereceğiniz tüm siparişlerde geçerli %15 indirim..."
                className="input-field"
                value={pushBody}
                onChange={(e) => setPushBody(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setPushModalOpen(false)}
                className="btn-secondary"
                disabled={sendingPush}
              >
                Vazgeç
              </button>
              <button
                onClick={handleSendPush}
                disabled={sendingPush}
                className="px-5 py-2.5 bg-pink-600 hover:bg-pink-500 text-white font-medium rounded-lg shadow-lg shadow-pink-600/30 transition flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sendingPush ? 'Gönderiliyor...' : 'Bildirimi Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
